import { type FC, type ReactElement, useMemo } from "react"

import { motion } from "framer-motion"
import map from "lodash-es/map"

import { type IUseTranslation, useTranslation } from "@/hooks"

import type { IParticipantSurveyModel as IModel } from "../api"

import { SurveysDataListItem as ListItem, TSurveysDataListItemProps } from "./SurveysDataListItem"
import type { THandleListItemClickFunction } from "./types"
import { surveysPageUiUtils as utils } from "./utils"

type TProps = {
    surveysData: IModel[]
    handleListItemClick: THandleListItemClickFunction
    handleListItemHover: THandleListItemClickFunction
}

const SurveysDataList: FC<TProps> = ({
    surveysData,
    handleListItemClick,
    handleListItemHover
}: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const headingListItem: TSurveysDataListItemProps = useMemo(
        (): TSurveysDataListItemProps => utils.getHeadingListItem(t),
        [t]
    )

    return (
        <motion.ul
            variants={{
                hidden: { opacity: 0 },
                visible: {
                    opacity: 1,
                    transition: { when: "beforeChildren", delayChildren: 0.4, staggerChildren: 0.1 }
                }
            }}
            initial="hidden"
            animate="visible"
            className="surveys__list"
        >
            {map(
                [headingListItem, ...surveysData],
                (model: IModel, idx: number): ReactElement => (
                    <ListItem
                        key={`survey-${model.id}`}
                        {...model}
                        handleClick={handleListItemClick}
                        handleHover={handleListItemHover}
                        tabIndex={idx !== 0 ? 0 : undefined}
                    />
                )
            )}
        </motion.ul>
    )
}

export { SurveysDataList as default, type TProps as TSurveysDataListProps }
